(() => {
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  const MOBILE_WIDTH = 749;

  /*
   * SAME-HEIGHT SLIDES — what changed and why:
   *
   * 1. autoHeight is always false — Swiper's autoHeight resizes the container
   *    to the tallest *active* slide only, which makes other slides jump.
   *    Keeping it false lets all slides share the same fixed row height.
   *
   * 2. useAutoHeight / hasMobileContainer path no longer overrides to true —
   *    the mobile-container variant previously enabled autoHeight on mobile;
   *    that caused unequal heights too, so it is removed.
   *
   * 3. CSS added to the injected <style> block:
   *    • .swiper-wrapper { align-items: stretch }  → wrapper row stretches
   *    • .swiper-slide   { height: auto }          → each slide fills the row
   *    These two rules are all that is needed; Swiper sets the wrapper height
   *    to the tallest slide automatically.
   *
   * 4. Image-load listener that called updateAutoHeight() is removed because
   *    autoHeight is no longer used.
   */

  /* ── Inject equal-height stylesheet once ───────────────────────────── */
  if (!document.getElementById('banner-carousel-equal-height-style')) {
    const style = document.createElement('style');
    style.id = 'banner-carousel-equal-height-style';
    style.textContent = `
      /* Make every slide the same height as the tallest one in the group */
      banner-carousel .swiper-wrapper {
        align-items: stretch;
      }
      banner-carousel .swiper-slide {
        height: auto;           /* overrides Swiper's inline px height */
        display: flex;
        flex-direction: column;
      }
      /* Let direct children of a slide fill the full slide height        */
      banner-carousel .swiper-slide > * {
        flex: 1 1 auto;
      }
    `;
    document.head.appendChild(style);
  }

  /* ── Swiper lazy-loader (unchanged) ────────────────────────────────── */
  let swiperPromise;

  const loadSwiper = () => {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (swiperPromise) return swiperPromise;

    swiperPromise = new Promise((resolve, reject) => {
      const rejectMissingSwiper = () => {
        reject(
          new Error(
            'Swiper is not loaded. Add the Swiper CDN script and stylesheet in theme.liquid.'
          )
        );
      };

      if (document.readyState === 'complete') {
        rejectMissingSwiper();
        return;
      }

      window.addEventListener(
        'load',
        () => {
          if (window.Swiper) {
            resolve(window.Swiper);
          } else {
            rejectMissingSwiper();
          }
        },
        { once: true }
      );
    });

    return swiperPromise;
  };

  /* ── Custom element ─────────────────────────────────────────────────── */
  class BannerCarousel extends HTMLElement {
    connectedCallback() {
      this.slideCount          = Number(this.dataset.slideCount || 0);
      this.desktopSlidesPerView = this.dataset.slidesPerView || '1';
      this.showArrows          = this.dataset.showArrows === 'true';
      this.showDots            = this.dataset.showDots   === 'true';
      this.hasMobileContainer  = this.classList.contains('banner-carousel--mobile-container');
      this.mediaQuery          = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.handleBreakpointChange = this.update.bind(this);
      this.handleResize = () => {
        window.requestAnimationFrame(() => this.update());
      };

      if (this.slideCount <= 1) return;

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.handleBreakpointChange);
      } else {
        this.mediaQuery.addListener(this.handleBreakpointChange);
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.handleResize);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.mediaQuery && this.handleBreakpointChange) {
        if (this.mediaQuery.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.handleBreakpointChange);
        } else {
          this.mediaQuery.removeListener(this.handleBreakpointChange);
        }
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      } else if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
      this.destroySwiper();
    }

    shouldUseSwiper() {
      return this.slideCount > 1;
    }

    isMobileLayout() {
      const carouselWidth = this.getBoundingClientRect().width;
      return this.mediaQuery.matches || (carouselWidth > 0 && carouselWidth <= MOBILE_WIDTH);
    }

    async update() {
      if (!this.shouldUseSwiper()) {
        this.destroySwiper();
        return;
      }

      const isMobileLayout = this.isMobileLayout();
      this.classList.toggle('banner-carousel--mobile-layout', isMobileLayout);

      const spv = isMobileLayout ? 1 : Number(this.desktopSlidesPerView) || 1;

      // autoHeight is always false — equal-height CSS handles sizing instead
      const useAutoHeight = false;

      if (
        this.swiper &&
        this.currentSlidesPerView === spv
      ) {
        this.swiper.update();
        return;
      }

      // Breakpoint changed — destroy and reinitialise
      if (this.swiper) {
        this.destroySwiper();
      }

      try {
        const Swiper = await loadSwiper();
        if (!this.isConnected || !this.shouldUseSwiper() || !Swiper) return;
        this.initSwiper(Swiper, spv, useAutoHeight);
      } catch (error) {
        console.error('Banner carousel failed to load Swiper.', error);
      }
    }

    initSwiper(Swiper, spv, useAutoHeight) {
      this.classList.add('banner-carousel--active');
      this.currentSlidesPerView = spv;
      this.currentAutoHeight    = useAutoHeight;

      // slidesPerGroup matches slidesPerView so ALL visible slides move
      // together when an arrow is clicked (not just one at a time)
      const spg = spv;

      this.swiper = new Swiper(this, {
        slidesPerView:  spv,
        slidesPerGroup: spg,
        autoHeight:     false,   // ← equal-height: always off
        speed:          600,
        loop:           this.slideCount > spv,
        watchOverflow:  true,
        autoplay: {
          delay:                7000,
          disableOnInteraction: false,
          pauseOnMouseEnter:    true,
        },
        keyboard: {
          enabled:         true,
          onlyInViewport:  true,
        },
        a11y: {
          enabled: true,
        },
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector('.banner-carousel__arrow--next'),
              prevEl: this.querySelector('.banner-carousel__arrow--prev'),
            }
          : false,
        pagination: this.showDots
          ? {
              el:        this.querySelector('.banner-carousel__pagination'),
              clickable: true,
            }
          : false,
      });
    }

    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.currentSlidesPerView = null;
      this.currentAutoHeight    = null;
      this.classList.remove('banner-carousel--active');
    }
  }

  if (!customElements.get('banner-carousel')) {
    customElements.define('banner-carousel', BannerCarousel);
  }
})();